import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "context/AuthContext";
import type { ExistingProvider } from "../types";
import { getProviderCapabilities } from "../utils/providerHelpers";
import { useProviderData } from "../hooks/useProviderData";
import { usePriorityOptions } from "../hooks/usePriorityOptions";
import { useTabNavigation } from "../hooks/useTabNavigation";
import { useProviderFormInitialization } from "../hooks/useProviderFormInitialization";
import { useProviderFormEffects } from "../hooks/useProviderFormEffects";
import { OnlinePaymentsConfigContext } from "./OnlinePaymentsConfigContext";
import { useFormSetup } from "./hooks/useFormSetup";
import { useValidationState } from "./hooks/useValidationState";
import type {
  OnlinePaymentsConfigContextType,
  OnlinePaymentsConfigProviderProps,
} from "./types";

export const OnlinePaymentsConfigProvider: React.FC<
  OnlinePaymentsConfigProviderProps
> = ({
  children,
  initialMode = "ADD",
  initialProvider = null,
  availableProviders: externalAvailableProviders,
  dealerProviders: externalDealerProviders,
  merchantInfo: externalMerchantInfo,
  readOnly = false,
}) => {
  const { dealerId } = useAuth();
  const mode = initialMode;
  const [existingProvider, setExistingProvider] =
    useState<ExistingProvider | null>(initialProvider);
  const [isLoading, setIsLoading] = useState(false);
  const [internalReadOnly, setInternalReadOnly] = useState(readOnly);

  useEffect(() => {
    setInternalReadOnly(readOnly);
  }, [readOnly]);

  const formState = useFormSetup();
  const {
    register,
    watch,
    setValue,
    control,
    handleSubmit,
    formErrors,
    reset,
    trigger,
    getValues,
    clearErrors,
    mcc,
    selectedProvider,
  } = formState;

  const {
    validationErrors,
    isValidationActive,
    setTabErrors,
    setFormErrors,
    hasAnyErrors,
    activateValidation,
    clearAllErrors,
  } = useValidationState();

  const providers = useProviderData(externalAvailableProviders);
  const priorityOptions = usePriorityOptions(
    externalDealerProviders,
    mode,
    existingProvider,
  );

  const capabilities = useMemo(
    () => getProviderCapabilities(selectedProvider),
    [selectedProvider],
  );

  const showCredentialsTab = !selectedProvider || capabilities.hasCredentials;
  const showBrandsTab = !selectedProvider || capabilities.hasBrands;

  useEffect(() => {
    register("brands", {
      validate: (value) => {
        if (!capabilities.brandsRequired) {
          return true;
        }
        if (!value || value.length === 0) {
          return "Pelo menos uma bandeira deve ser adicionada";
        }
        return true;
      },
    });
  }, [register, capabilities.brandsRequired]);

  useProviderFormEffects({
    state: formState,
    merchantInfo: externalMerchantInfo,
    providers,
  });

  useProviderFormInitialization({
    state: formState,
    mode,
    existingProvider: initialProvider,
    providers,
    priorityOptions,
    merchantInfo: externalMerchantInfo,
  });

  const tabNavigation = useTabNavigation(validationErrors, {
    showCredentialsTab,
    showBrandsTab,
  });

  const value = useMemo<OnlinePaymentsConfigContextType>(
    () => ({
      mode,
      isLoading,
      setIsLoading,
      existingProvider,
      setExistingProvider,
      internalReadOnly,
      setInternalReadOnly,
      dealerId,
      errors: validationErrors,
      setTabErrors,
      hasAnyErrors,
      clearAllErrors,
      isValidationActive,
      activateValidation,
      setFormErrors,
      register,
      watch,
      setValue,
      control,
      formErrors,
      trigger,
      getValues,
      clearErrors,
      handleSubmit,
      reset,
      selectedProvider,
      providers,
      priorityOptions,
      showCredentialsTab,
      showBrandsTab,
      mcc,
      currentTab: tabNavigation.currentTab,
      previousTab: tabNavigation.previousTab,
      isAnimating: tabNavigation.isAnimating,
      tabErrors: tabNavigation.tabErrors,
      handleChangeTab: tabNavigation.handleChangeTab,
      setCurrentTab: tabNavigation.setCurrentTab,
      getAnimationDirection: tabNavigation.getAnimationDirection,
    }),
    [
      mode,
      isLoading,
      existingProvider,
      internalReadOnly,
      dealerId,
      validationErrors,
      setTabErrors,
      hasAnyErrors,
      clearAllErrors,
      isValidationActive,
      activateValidation,
      setFormErrors,
      register,
      watch,
      setValue,
      control,
      formErrors,
      trigger,
      getValues,
      clearErrors,
      handleSubmit,
      reset,
      selectedProvider,
      providers,
      priorityOptions,
      showCredentialsTab,
      showBrandsTab,
      mcc,
      tabNavigation,
    ],
  );

  return (
    <OnlinePaymentsConfigContext.Provider value={value}>
      {children}
    </OnlinePaymentsConfigContext.Provider>
  );
};

export {
  useOnlinePaymentsConfig,
  useProviderFormContext,
  useTabNavigationContext,
  useValidation,
} from "./contextHooks";
